import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '@/context/AuthContext';
import { LOGO_URL, COMPANY_NAME } from '@/config/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Mail, Lock, AlertCircle, CheckCircle2, Wrench } from 'lucide-react';
import { repairPwaAndReload } from '@/lib/pwaRepair';

const REMEMBERED_EMAIL_KEY = 'cr_remembered_email';
const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LoginPage() {
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBERED_EMAIL_KEY) || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // iter88: dialog "Password dimenticata"
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (email && email.includes('@')) localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      navigate(user.role === 'admin' ? '/admin' : '/worker');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const openForgotDialog = () => {
    setForgotEmail(email || '');
    setForgotSent(false);
    setForgotError('');
    setForgotOpen(true);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    if (!forgotEmail || !forgotEmail.includes('@')) {
      setForgotError('Inserisci un indirizzo email valido.');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, { email: forgotEmail.trim().toLowerCase() });
      setForgotSent(true);
    } catch (err) {
      // Il backend è progettato per rispondere sempre 200 (no user enumeration).
      // In caso di errore di rete/timeout mostriamo un messaggio generico.
      setForgotError('Impossibile inviare la richiesta. Riprova tra qualche minuto.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200 mb-4">
            <img src={LOGO_URL} alt="CR" className="h-28 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">{COMPANY_NAME}</h1>
          <p className="text-sm text-slate-500 max-w-sm">
            Gestionale completo per la gestione dei cantieri, rapportini, contabilità e documenti.
          </p>
          <p className="text-xs text-blue-600 mt-2 font-medium">30 Anni di Esperienza | 1996 - 2026</p>
        </div>

        <Card className="border-slate-200 shadow-none">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold tracking-tight">Accedi</CardTitle>
            <CardDescription className="text-slate-500">Inserisci le tue credenziali per accedere</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-100 border border-red-200 rounded-md" data-testid="login-error">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" /><span>{error}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="email" type="email" autoComplete="username" placeholder="nome@esempio.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 border-slate-200" required data-testid="login-email-input" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <button
                    type="button"
                    onClick={openForgotDialog}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    data-testid="login-forgot-password-btn"
                  >
                    Hai dimenticato la password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="password" type="password" autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 border-slate-200" required data-testid="login-password-input" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800 font-medium" disabled={loading} data-testid="login-submit-btn">
                {loading ? 'Accesso in corso...' : 'Accedi'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* iter90b: Ripara app anche prima del login (per PWA bloccate) */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Riparare l\'app? Verranno pulite cache e Service Worker e la pagina verrà ricaricata. I tuoi dati sono al sicuro.')) {
                repairPwaAndReload();
              }
            }}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-amber-700 hover:underline"
            data-testid="login-repair-app-btn"
          >
            <Wrench className="h-3.5 w-3.5" />
            Problemi di visualizzazione? Ripara l&apos;app
          </button>
        </div>
      </div>

      {/* Dialog Password Dimenticata */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Password dimenticata</DialogTitle>
            <DialogDescription>
              Inserisci l&apos;email del tuo account. Se registrata, riceverai un link per reimpostare la password valido per 1 ora.
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="forgot-password-success">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5 text-emerald-600" />
                <div>
                  <div className="font-semibold mb-1">Richiesta inviata</div>
                  <div>
                    Se <strong>{forgotEmail}</strong> è registrata nel nostro sistema, riceverai a breve un&apos;email con il link per reimpostare la password. Controlla anche la cartella spam.
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  className="w-full bg-slate-900 text-white hover:bg-slate-800"
                  onClick={() => setForgotOpen(false)}
                  data-testid="forgot-password-close-btn"
                >
                  Chiudi
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleForgotSubmit} className="space-y-4">
              {forgotError && (
                <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-100 border border-red-200 rounded-md" data-testid="forgot-password-error">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{forgotError}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    autoComplete="username"
                    placeholder="nome@esempio.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="pl-10 border-slate-200"
                    required
                    autoFocus
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotOpen(false)}
                  disabled={forgotLoading}
                  data-testid="forgot-password-cancel-btn"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  disabled={forgotLoading}
                  data-testid="forgot-password-submit-btn"
                >
                  {forgotLoading ? 'Invio in corso...' : 'Invia link di reset'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
